/**
 * Select primitive.
 *
 * Styled single-select dropdown wrapped over `@radix-ui/react-select`.
 * Radix owns the headless behaviour — listbox/option ARIA roles and
 * `aria-selected` association, typeahead, Up/Down/Home/End keyboard
 * navigation, Space/Enter selection, Escape dismissal, the portal
 * mount, the controlled/uncontrolled value state (`value` /
 * `defaultValue` / `onValueChange`), and the Popper-substrate
 * positioning. Our job is to pin token-backed styling onto Radix's
 * primitives so the dropdown reads as a sibling of the `Input`
 * primitive (same border, radius, surface, focus ring, and disabled
 * treatment) without adding our own behaviour on top.
 *
 * It is the styled replacement for the native `<select>` elements the
 * admin CMS forms ship today (024-cms US9 visual-parity). This module
 * is the primitive only — a separate task adopts it at the call sites.
 *
 * Compound-component shape mirrors `Tabs.tsx` / `Sheet.tsx` /
 * `Dialog.tsx` exactly: each subcomponent is a **dedicated named
 * export** AND the module exposes an aggregate `Select` object via
 * `export const Select = { Root, Trigger, Value, Content, Item }`.
 * Callers prefer the aggregate
 * (`<Select.Root><Select.Trigger><Select.Value/></Select.Trigger>…</Select.Root>`)
 * for readability; the dedicated named exports exist so bundlers can
 * tree-shake at the subcomponent level and tests can import narrowly.
 * `Content` embeds Radix's `Portal` internally so the public API stays
 * trap-free; `Root` and `Value` are passed through unstyled (Radix's
 * elements are the right ones and need no token-styling layer).
 * `Trigger`, `Content`, and `Item` are thin wrappers that inject
 * token-backed classes before forwarding remaining props — `Trigger`
 * also forwards
 * `id` / `aria-invalid` / `disabled` / `data-testid` / `className`
 * through `ComponentProps` so a Form layer can wire the field's label,
 * description, and error association onto the trigger.
 *
 * The `Group` / `Label` / `Separator` subcomponents Radix offers are
 * deliberately omitted (YAGNI — the CMS field selects this replaces are
 * flat option lists with no grouping); a future surface that needs
 * grouped options extends this module with the corresponding thin
 * wrappers.
 *
 * Icons are the EXISTING omaplus inline-SVG icons (`ChevronDownIcon` /
 * `CheckIcon` from `../shell/icons.js`) — no icon library is added
 * (D-043 forbids `lucide-react`; the proto's `select.tsx` imports from
 * it, this module does not). The chevron renders via `SelectPrimitive.Icon
 * asChild` (Radix's affordance slot *becomes* the glyph); the check renders
 * as a child of `SelectPrimitive.ItemIndicator` (no `asChild` — the
 * indicator owns its slot and the glyph sits inside it). Both glyphs are
 * `aria-hidden` (icons.tsx `baseSvgProps`), so neither the chevron's
 * `<title>` nor the check contributes to the trigger / option accessible
 * name — the field label and the selected value text do.
 *
 * Styling is token-backed only. The trigger mirrors `Input.tsx`'s
 * surface (`bg-background`, `border border-border`, `rounded-md`,
 * `min-h-11` for the FR-036 44px tap target, and the FR-033 sanctioned
 * `focus-visible:ring-2 ring-offset-2 ring-ring` focus treatment). The
 * highlighted item paints the brand **primary** token surface with its
 * foreground (`data-[highlighted]:bg-primary data-[highlighted]:text-primary-foreground`)
 * — NOT a hardcoded hex; the proto's `#619C38` literal is deliberately
 * not copied (FR-002). Every class below is a token-backed Tailwind
 * utility — no arbitrary-bracket classes (FR-047), no raw colour
 * literals (FR-002). The `data-[state]` / `data-[highlighted]` /
 * `data-[disabled]` state-modifier brackets are first-class Tailwind
 * variant syntax (the no-token-bypass check excludes them via its
 * trailing-`:` lookahead), not arbitrary-value classes.
 *
 * Refs are plain props per React 19 conventions; `ComponentProps`
 * carries Radix's own `ref` typing through, so no `forwardRef` wrapper
 * is needed (matching `Tabs` / `Input` / `Sheet`). The proto's file
 * uses `React.forwardRef`; React 19 makes that ceremony unnecessary.
 *
 * `Content` defaults `position="popper"` so the listbox anchors under
 * the trigger (rather than overlaying it), matching the prototype's
 * dropdown posture and the `Input`-sibling closed/open visual target.
 */

import * as SelectPrimitive from '@radix-ui/react-select';
import type { ComponentProps } from 'react';

import { CheckIcon, ChevronDownIcon } from '../shell/icons.js';
import { cn } from './utils.js';

/**
 * Trigger size posture. `default` is the full-width, 44px (FR-036)
 * Input-sibling field select every adopted CMS form uses; `sm` is the
 * compact toolbar posture (auto width, 36px, text-sm) for a control that
 * sits inline beside other compact chrome (the preview zoom, next to the
 * device toggle). Mirrors `Button`'s `sm`/`md` size convention.
 */
export type SelectTriggerSize = 'default' | 'sm' | 'xs';

/**
 * Trigger visual variant. `field` (default) is the neutral Input-sibling outline
 * (1px border token, medium radius) every adopted form select uses. `create` is
 * the prototype's "Uusi" create affordance — a brand-green pill (2px
 * `border-primary`, fully rounded) that reads as a distinct call to action, not
 * a form field. Border + radius live in the variant (NOT a caller `className`)
 * because `cn()` is a plain concat: a same-property `className` override would
 * resolve by Tailwind's compile order, not call order.
 */
export type SelectTriggerVariant = 'field' | 'create' | 'pill';

export type SelectTriggerProps = ComponentProps<typeof SelectPrimitive.Trigger> & {
  readonly size?: SelectTriggerSize;
  readonly variant?: SelectTriggerVariant;
};

/**
 * Popper width posture. `trigger` (default) pins the listbox to the trigger's
 * measured width — flush under a full-width field select. `fit` sizes the
 * listbox to its content (floored at the trigger width) — for a narrow-trigger
 * dropdown like the "Uusi" create menu, whose option labels are wider than the
 * compact trigger and would otherwise clip.
 */
export type SelectContentWidthMode = 'trigger' | 'fit';

export type SelectContentProps = ComponentProps<typeof SelectPrimitive.Content> & {
  readonly widthMode?: SelectContentWidthMode;
};
export type SelectItemProps = ComponentProps<typeof SelectPrimitive.Item> & {
  /**
   * Whether the row reserves the left check-indicator column. `true` (default)
   * for value pickers (the selected option shows a check); `false` for an
   * action dropdown that holds no value (the "Uusi" create menu), which drops
   * the check + its reserved column so labels are not pushed right.
   */
  readonly indicator?: boolean;
  /**
   * The row's horizontal inset. `default` reserves the wide check column on the
   * left; `narrow` indents 32px on the left and 8px on the right, for a short
   * set under a narrow trigger where the default's 48px makes the row wider
   * than its content needs. Labels are left-aligned in both.
   */
  readonly inset?: SelectItemInset;
};

/** See {@link SelectItemProps.inset}. */
export type SelectItemInset = 'default' | 'narrow';

// Trigger mirrors `Input.tsx`'s surface so the closed dropdown reads as
// an Input with a trailing chevron: the same border/radius/background,
// the FR-033 sanctioned focus ring, and the shared disabled treatment.
// `[&>span]:line-clamp-1` keeps a long selected value to one line — it
// targets every direct `<span>` child, which at every call site is just the
// Radix-emitted value `<span>` (`<Select.Value>`); a consumer passing custom
// trigger children with their own direct `<span>` would clamp those too (the
// shadcn reference scopes it the same way). `cursor-pointer` is set
// explicitly because Tailwind v4 preflight no longer applies it to
// buttons (same rationale Tabs documents on its trigger).
//
// Width / min-height / padding / text-scale are SIZE-specific and live in
// `triggerSizeClasses`, picked through the typed `size` prop — NOT a
// caller `className`. `cn()` is a plain concat (no tailwind-merge), so a
// same-property override passed via `className` would resolve by
// Tailwind's compile order, not call order; a typed variant is the only
// reliable way to re-size the trigger.
const triggerBaseClasses =
  'inline-flex items-center justify-between gap-2 ' +
  'bg-background text-foreground ' +
  'cursor-pointer transition-colors ' +
  'data-[placeholder]:text-muted-foreground ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring ' +
  'disabled:opacity-50 disabled:cursor-not-allowed ' +
  '[&>span]:line-clamp-1';

// Border + radius are variant-specific (kept out of the base so a variant swaps
// both without a `cn()` same-property collision). `field` is the Input-sibling
// outline; `create` is the prototype's brand-green "Uusi" pill.
const triggerVariantClasses: Record<SelectTriggerVariant, string> = {
  field: 'border border-border rounded-md',
  create: 'rounded-full border-2 border-primary',
  // `pill`: the admin header band's neutral outline pill, matching the
  // Päävalikko link and the Arkisto button it sits beside so the three read as
  // one cluster. Border and radius live HERE rather than in a caller
  // `className` for the reason the variant type already states: `cn()` is a
  // plain concat, so a same-property override from a caller would not win.
  pill: 'rounded-full border border-border bg-background',
};

// `default`: the full-width 44px (FR-036) field posture every adopted
// form select relies on. `sm`: the compact toolbar posture — `w-auto`
// overrides the field default's `w-full`, and `min-h-9 px-3 py-1 text-sm`
// mirrors `Button`'s `sm` size so the two read as siblings in a toolbar.
const triggerSizeClasses: Record<SelectTriggerSize, string> = {
  default: 'w-full min-h-11 px-3 py-2 text-base',
  sm: 'w-auto min-h-9 px-3 py-1 text-sm',
  // `xs`: 32px (`min-h-6` in omaplus's retuned spacing scale), mirroring
  // `Button`'s `xs` so the preview zoom matches the 32px device toggle + pills.
  xs: 'w-auto min-h-6 px-3 py-1 text-sm',
};

const triggerIconBaseClasses = 'shrink-0 text-muted-foreground';
// The chevron tracks the trigger's text-scale so it stays proportionate.
const triggerIconSizeClasses: Record<SelectTriggerSize, string> = {
  default: 'size-5',
  sm: 'size-4',
  xs: 'size-4',
};

// Content: a bordered, rounded, shadowed popover anchored under the
// trigger. `bg-popover`/`text-popover-foreground` is the Radix-surface
// token pair (matches Sheet/Dialog). `overflow-hidden` clips the
// viewport to the rounded corners; `min-w-32` gives a sensible floor
// for short option lists. `z-50` matches the other floating primitives.
const contentClasses =
  'z-50 max-h-96 min-w-32 overflow-hidden rounded-md ' +
  'border border-border bg-popover text-popover-foreground shadow-md';

// When `position="popper"`, Radix exposes the trigger's measured width
// as a CSS variable; matching the viewport width to it makes the open
// list line up flush under the trigger. The width is referenced through
// Radix's own published custom property via an inline style (NOT an
// arbitrary-bracket Tailwind class), keeping the no-token-bypass check
// satisfied.
const viewportClasses = 'p-1';

const viewportPopperStyle = {
  width: 'var(--radix-select-trigger-width)',
} as const;

// `widthMode="fit"`: the viewport takes its CONTENT's width (the longest
// option), floored by whichever is wider of the trigger and the popup itself.
// The trigger is therefore still read here -- as a floor, never as the width:
// the default arm PINS the viewport to it, and a narrow-trigger dropdown (the
// "Uusi" create menu) whose options are wider than that compact trigger would
// clip under the pin, which is why this arm exists.
//
// THE `100%` IN THE FLOOR IS LOAD-BEARING, and it is what makes the rows FILL
// the popup instead of huddling inside it. `Content` carries `min-w-32`, so a
// short option set leaves the popup at 128px while `max-content` shrank the
// viewport to the longest label -- rows narrower than the surface they sit on,
// with the highlight bar stopping short of the popup's edge. Measured on the
// content-language switcher: rows came out 98.09px in a 128px popup, against
// the prototype's 118 in the same 128. With `100%` in the floor the rows land
// at 118.00 and the wide case is untouched (an option set past the floor still
// grows: 324.66px rows in a 334.66px popup, measured either way).
const viewportPopperFitStyle = {
  minWidth: 'max(var(--radix-select-trigger-width), 100%)',
  width: 'max-content',
} as const;

// Item: a full-width row. Horizontal padding is NOT here: it is chosen once per
// arm in `Item`, because two padding utilities on one element resolve by
// stylesheet order rather than by the order written, so a base plus an override
// is a race. Highlighted paints the brand primary token surface + its
// foreground; the disabled treatment matches the rest of the kit.
const itemBaseClasses =
  'relative flex w-full cursor-pointer select-none items-center gap-2 rounded-sm ' +
  'py-2 text-base outline-none ' +
  'data-[highlighted]:bg-primary data-[highlighted]:text-primary-foreground ' +
  'data-[disabled]:pointer-events-none data-[disabled]:opacity-50';

// `inset="narrow"`: a 32px left indent and an 8px right one, the prototype's
// own figures for the content-language switcher. They are ASYMMETRIC on purpose
// and the asymmetry is not a width control: the row's width comes from the
// popup it fills (see the fit-width note above), so the left pad sets where the
// labels line up and the right one is only the label's breathing room.
//
// 32px IS `px-6`, NOT `px-8`, and that is the one thing here worth writing
// down: this spacing scale is retuned and non-linear, and Tailwind v4 emits it
// at build time so no file states it. Measured in a browser — 2 is 8px, 3 is
// 12px, 4 is 16px, 5 is 24px, 6 is 32px, 7 is NOT GENERATED AT ALL, 8 is 48px.
//
// Labels are LEFT-ALIGNED, as in the default arm. Centring this arm was tried
// and rejected: it puts each label in the middle of its own row, so the three
// left edges scatter by the label-width spread instead of sharing one edge.
const itemIndicatorWrapperClasses = 'absolute left-2 inline-flex items-center justify-center';

const itemIndicatorIconClasses = 'size-4';

export const Root = SelectPrimitive.Root;

export function Trigger({
  className,
  children,
  size = 'default',
  variant = 'field',
  asChild = false,
  ...rest
}: SelectTriggerProps) {
  // `asChild` lets a composed control own its semantic element and visual
  // treatment. Radix's Slot requires exactly one child, so this branch cannot
  // append the default chevron sibling; the composed child owns its icon too.
  if (asChild) {
    return (
      <SelectPrimitive.Trigger asChild className={className} {...rest}>
        {children}
      </SelectPrimitive.Trigger>
    );
  }

  // `size` / `variant` are consumed here (typed variants) and NOT forwarded —
  // `size` is an invalid attribute on the underlying `<button>`.
  return (
    <SelectPrimitive.Trigger
      className={cn(
        triggerBaseClasses,
        triggerVariantClasses[variant],
        triggerSizeClasses[size],
        className,
      )}
      {...rest}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDownIcon className={cn(triggerIconBaseClasses, triggerIconSizeClasses[size])} />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

// Passed through unstyled like `Root` — Radix's `Value` injects no token
// classes (`<Select.Value placeholder=… />`).
export const Value = SelectPrimitive.Value;

export function Content({
  className,
  children,
  position = 'popper',
  widthMode = 'trigger',
  ...rest
}: SelectContentProps) {
  // `widthMode` selects the popper width style and is NOT forwarded to Radix.
  const popperStyle =
    position === 'popper'
      ? widthMode === 'fit'
        ? viewportPopperFitStyle
        : viewportPopperStyle
      : undefined;
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        position={position}
        className={cn(contentClasses, className)}
        {...rest}
      >
        <SelectPrimitive.Viewport className={viewportClasses} style={popperStyle}>
          {children}
        </SelectPrimitive.Viewport>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
}

export function Item({
  className,
  children,
  indicator = true,
  inset = 'default',
  ...rest
}: SelectItemProps) {
  // `indicator` and `inset` are consumed here (they gate the check column and
  // the row's padding) and NOT forwarded.
  //
  // The padding is chosen ONCE rather than layered, because two padding
  // utilities on one element resolve by stylesheet order rather than by the
  // order written here — so a base value plus an override is a race.
  const paddingClasses = inset === 'narrow' ? 'pl-6 pr-2' : indicator ? 'pl-8 pr-3' : 'px-3';
  return (
    <SelectPrimitive.Item className={cn(itemBaseClasses, paddingClasses, className)} {...rest}>
      {indicator ? (
        <span className={itemIndicatorWrapperClasses}>
          <SelectPrimitive.ItemIndicator>
            <CheckIcon className={itemIndicatorIconClasses} />
          </SelectPrimitive.ItemIndicator>
        </span>
      ) : null}
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
}

export const Select = {
  Root,
  Trigger,
  Value,
  Content,
  Item,
} as const;
